import { createEffect, createEvent, createStore, sample, combine } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { ITasksContract } from "@features/tasks/model";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { ICurrentCourseContract } from "@features/courses/ui/current";
import { ICurrentLessonContract } from "@features/lessons/ui/current";
import { ICurrentTopicContract } from "@features/topics/ui/current";

import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { TaskStarted, TaskCompleted, TaskFailed } from "@features/shared/events";

type TaskLayoutConfig = {
    taskId: string;
    answer: string | string[];
    options: string[];
    taskType?: string;
    isSingleAnswer: boolean;
    isSingleOption: boolean;
    hasAnyHintTrigger: boolean;
    showHintButtonAfterSuccess: boolean;
    showAnswerBubbles: boolean;
    showHintButtonAfterSubmit: boolean;
    inlineHintEnabled: boolean;
    forceShowHint: boolean;
    hintText?: string;
    whyText?: string;
    checkAnswerCorrect?: (selected: any, answer: string | string[], taskType?: string) => boolean;
    showSubmitButton?: (selected: any, answer: string | string[], taskType?: string) => boolean;
    autoSubmitOnSelect?: boolean;
    allowContinueAfterWrongAnswer?: boolean;
};

type TaskInstanceState = {
    selected: any | null;
    submitted: boolean;
    showHint: boolean;
    showCorrectAnswer: boolean;
    showWrongAnswer: boolean;
    hasStarted: boolean;
    revealedOptions?: Record<string, 'correct' | 'incorrect'>;
    dropdownGapExcludedById?: Record<string, string[]>;
    dropdownGapLockedCorrect?: Record<string, string>;
    userInteractedAfterSubmit?: boolean;
};

type OptionStatus = "default" | "selected" | "correct" | "incorrect" | "revealed-correct";

const optionStatusCache: Record<string, Record<string, OptionStatus>> = {};

const isOptionStatusEqual = (
    prev: Record<string, OptionStatus> | undefined,
    next: Record<string, OptionStatus>
): boolean => {
    if (!prev) return false;
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) return false;
    for (let i = 0; i < nextKeys.length; i += 1) {
        const key = nextKeys[i];
        if (prev[key] !== next[key]) return false;
    }
    return true;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const tryParseObject = (json: string): Record<string, unknown> | null => {
    try {
        const parsed = JSON.parse(json);
        return isPlainObject(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const extractEventValue = (payload: any, fallback: string) =>
    payload && payload.target && typeof payload.target.value !== "undefined" ? payload.target.value : fallback;

const isAnswerCorrect = (
    sel: any,
    ans: string | string[],
    taskType?: string,
    checkAnswerCorrect?: (selected: any, answer: string | string[], taskType?: string) => boolean,
    allowContinueAfterWrongAnswer?: boolean
): boolean => {
    if (typeof checkAnswerCorrect === "function") return checkAnswerCorrect(sel, ans, taskType);
    if (Array.isArray(ans)) {
        if (!Array.isArray(sel)) return false;
        if (taskType === "sort-answers") {
            if (ans.length !== sel.length) return false;
            for (let i = 0; i < ans.length; i++) {
                if (ans[i] !== sel[i]) return false;
            }
            return true;
        }
        if (ans.length !== sel.length) return false;
        return ans.every((a) => sel.includes(a));
    }
    if (isPlainObject(sel)) {
        const parsed = tryParseObject(String(ans));
        if (!parsed) return false;
        const selKeys = Object.keys(sel);
        const ansKeys = Object.keys(parsed);
        if (selKeys.length !== ansKeys.length) return false;
        for (const key of ansKeys) {
            if ((sel as any)[key] !== (parsed as any)[key]) return false;
        }
        return true;
    }
    return sel === ans;
};

const defaultStateForAnswer = (answer: string | string[]): TaskInstanceState => ({
    selected: Array.isArray(answer) ? [] : null,
    submitted: false,
    showHint: false,
    showCorrectAnswer: false,
    showWrongAnswer: false,
    hasStarted: false,
    revealedOptions: {},
    dropdownGapExcludedById: {},
    dropdownGapLockedCorrect: {},
    userInteractedAfterSubmit: false,
});

export const taskLayoutInstanceMounted = createEvent<TaskLayoutConfig>();
export const taskLayoutInstanceUnmounted = createEvent<{ taskId: string }>();
export const taskLayoutOptionChanged = createEvent<{ taskId: string; option?: string; payload?: any }>();
export const taskLayoutSubmitClicked = createEvent<{ taskId: string }>();
export const taskLayoutResetClicked = createEvent<{ taskId: string; silent?: boolean }>();
export const taskLayoutHintClicked = createEvent<{ taskId: string }>();
// One event per check with the verdict, for sounds / XP. `wasAlreadyCorrect` marks a re-check of a solved task.
export const taskAnswerChecked = createEvent<{
    taskId: string;
    lessonId: number;
    courseId: number;
    topicId: number;
    taskType?: string;
    isCorrect: boolean;
    usesSavedAnswer: boolean;
    wasAlreadyCorrect: boolean;
}>();

const applyEnrollmentSync = createEvent<Record<string, Partial<TaskInstanceState>>>();
const applyStatePatch = createEvent<{ taskId: string; patch: Partial<TaskInstanceState> }>();
const setUsesSavedAnswer = createEvent<{ taskId: string; value: boolean }>();

export const $taskLayoutConfigById = createStore<Record<string, TaskLayoutConfig>>({})
    .on(taskLayoutInstanceMounted, (map, cfg) => ({ ...map, [cfg.taskId]: cfg }))
    .on(taskLayoutInstanceUnmounted, (map, { taskId }) => {
        const { [taskId]: _, ...rest } = map;
        return rest;
    });

export const $taskLayoutStateById = createStore<Record<string, TaskInstanceState>>({})
    .on(taskLayoutInstanceMounted, (map, cfg) => {
        if (map[cfg.taskId]) return map;
        return { ...map, [cfg.taskId]: defaultStateForAnswer(cfg.answer) };
    })
    .on(applyEnrollmentSync, (map, batch) => {
        const next = { ...map };
        for (const id of Object.keys(batch)) {
            const prev = next[id] ?? defaultStateForAnswer(({} as any).answer);
            next[id] = { ...prev, ...batch[id] } as TaskInstanceState;
        }
        return next;
    })
    .on(applyStatePatch, (map, { taskId, patch }) => ({ ...map, [taskId]: { ...(map[taskId] ?? defaultStateForAnswer("")), ...patch } }))
    .on(taskLayoutInstanceUnmounted, (map, { taskId }) => {
        const { [taskId]: _, ...rest } = map;
        return rest;
    });

export const $usesSavedAnswerById = createStore<Record<string, boolean>>({})
    .on(setUsesSavedAnswer, (map, { taskId, value }) => {
        const prev = map[taskId];
        if (value) {
            if (typeof prev !== "undefined") return map;
            return { ...map, [taskId]: true };
        }
        return { ...map, [taskId]: false };
    })
    .on(applyEnrollmentSync, (map, batch) => {
        const next = { ...map };
        for (const id of Object.keys(batch)) {
            if (typeof next[id] === "undefined") {
                next[id] = true;
            }
        }
        return next;
    })
    .on(taskLayoutResetClicked, (map, { taskId }) => ({ ...map, [taskId]: false }))
    .on(taskLayoutInstanceUnmounted, (map, { taskId }) => {
        const { [taskId]: _, ...rest } = map;
        return rest;
    });

export const $isCorrectById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];
        const prev = st[id];
        const next = isAnswerCorrect(prev?.selected, c.answer, c.taskType, c.checkAnswerCorrect, c.allowContinueAfterWrongAnswer);
        result[id] = next;
    }
    return result;
});

export const $isAllSelectedById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const state = st[id];
        if (!state) {
            result[id] = false;
            continue;
        }
        const selected = state.selected;
        if (!selected) {
            result[id] = false;
            continue;
        }
        let selectedObj: Record<string, unknown> | null = null;
        if (isPlainObject(selected)) {
            selectedObj = selected as Record<string, unknown>;
        } else if (typeof selected === "string") {
            selectedObj = tryParseObject(selected);
        }
        if (selectedObj) {
            let expectedCount = 0;
            const answer = cfg[id].answer;
            if (typeof answer === "string") {
                const parsedAnswer = tryParseObject(answer);
                if (parsedAnswer) expectedCount = Object.keys(parsedAnswer).length;
            }
            if (expectedCount === 0) expectedCount = Object.keys(selectedObj).length;
            const nonEmptyValues = Object.values(selectedObj).filter((value) => value !== "" && value !== null && typeof value !== "undefined");
            result[id] = nonEmptyValues.length === expectedCount;
            continue;
        }
        if (Array.isArray(selected)) {
            result[id] = selected.length > 0;
            continue;
        }
        result[id] = Boolean(selected);
    }
    return result;
});

export const $shouldShowSubmitById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];
        const state = st[id];
        const submitted = state?.submitted ?? false;

        if (submitted) {
            result[id] = false;
            continue;
        }

        if (c.autoSubmitOnSelect) {
            result[id] = false;
            continue;
        }

        if (typeof c.showSubmitButton === "function") {
            result[id] = c.showSubmitButton(state?.selected, c.answer, c.taskType);
            continue;
        }

        result[id] = true;
    }
    return result;
});

export const $shouldDisableSubmitById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById, isAllSelected: $isAllSelectedById }, ({ cfg, st, isAllSelected }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];

        if (c.taskType === "sort-answers") {
            result[id] = false;
            continue;
        }

        result[id] = !(isAllSelected[id] ?? false);
    }
    return result;
});

export const $shouldShowHintBtnById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];
        const s = st[id];
        const hasAnyHintTrigger = c.hasAnyHintTrigger;
        if (!hasAnyHintTrigger) {
            result[id] = false;
            continue;
        }
        if (c.showHintButtonAfterSuccess) {
            result[id] = Boolean(s?.showCorrectAnswer);
            continue;
        }
        if (c.showHintButtonAfterSubmit) {
            result[id] = Boolean(s?.submitted);
            continue;
        }
        result[id] = true;
    }
    return result;
});

export const $effectiveShowHintById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const result: Record<string, boolean> = {};
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];
        const s = st[id];
        result[id] = c.inlineHintEnabled ? Boolean(c.forceShowHint || s?.showHint) : false;
    }
    return result;
});

export const $optionStatusMapById = combine({ cfg: $taskLayoutConfigById, st: $taskLayoutStateById }, ({ cfg, st }) => {
    const map: Record<string, Record<string, OptionStatus>> = {};
    const seen = new Set<string>();
    for (const id of Object.keys(cfg)) {
        const c = cfg[id];
        const s = st[id];
        const result: Record<string, OptionStatus> = {};
        const isSubmittedGlobal = Boolean(s?.showCorrectAnswer || s?.showWrongAnswer);
        const revealedOptions = c.allowContinueAfterWrongAnswer ? (s?.revealedOptions || {}) : {};

        for (const opt of c.options) {
            const isSingleOption = c.isSingleOption;
            const checked = isSingleOption ? false : Array.isArray(s?.selected) ? (s?.selected ?? []).includes(opt) : s?.selected === opt;

            if (c.allowContinueAfterWrongAnswer && revealedOptions[opt]) {
                result[opt] = revealedOptions[opt];
                continue;
            }

            const isCorrect = isSingleOption
                ? isSubmittedGlobal && !Array.isArray(c.answer) && s?.selected === c.answer
                : isSubmittedGlobal && checked && (Array.isArray(c.answer) ? (c.answer as string[]).includes(opt) : opt === c.answer);
            const isIncorrect = isSingleOption
                ? isSubmittedGlobal && Boolean(s?.selected) && !isCorrect
                : isSubmittedGlobal && checked && (Array.isArray(c.answer) ? !(c.answer as string[]).includes(opt) : opt !== c.answer);
            const status: OptionStatus = isCorrect ? "correct" : isIncorrect ? "incorrect" : checked ? "selected" : "default";
            result[opt] = status;
        }

        if (
            Boolean(s?.showCorrectAnswer) &&
            !Array.isArray(c.answer) &&
            typeof c.answer === "string" &&
            !c.isSingleOption
        ) {
            for (const opt of c.options) {
                if (c.allowContinueAfterWrongAnswer && revealedOptions[opt]) continue;
                if (opt !== c.answer) {
                    result[opt] = "incorrect";
                }
            }
        }

        if (
            Boolean(s?.showCorrectAnswer) &&
            Array.isArray(c.answer) &&
            !c.isSingleOption
        ) {
            for (const opt of c.options) {
                if (c.allowContinueAfterWrongAnswer && revealedOptions[opt]) continue;
                if (!(c.answer as string[]).includes(opt)) {
                    result[opt] = "incorrect";
                }
            }
        }

        const cached = optionStatusCache[id];
        const next = isOptionStatusEqual(cached, result) ? cached! : result;
        map[id] = next;
        optionStatusCache[id] = next;
        seen.add(id);
    }
    for (const id of Object.keys(optionStatusCache)) {
        if (!seen.has(id)) delete optionStatusCache[id];
    }
    return map;
});

const reportTaskStateFx = createEffect<{
    lessonId: number;
    taskId: string;
    status: "completed" | "failed" | "reset";
    answer: string;
}, void>();

const trackTaskStartedFx = createEffect<{ courseId: number; topicId: number; lessonId: number; taskId: string; taskType?: string }, void>(({ courseId, topicId, lessonId, taskId, taskType }) => {
    if (!taskType) return;
    TaskStarted(courseId, topicId, lessonId, taskId, taskType);
});

const trackTaskCompletedFx = createEffect<{ courseId: number; topicId: number; lessonId: number; taskId: string; taskType?: string }, void>(({ courseId, topicId, lessonId, taskId, taskType }) => {
    if (!taskType) return;
    TaskCompleted(courseId, topicId, lessonId, taskId, taskType);
});

const trackTaskFailedFx = createEffect<{ courseId: number; topicId: number; lessonId: number; taskId: string; taskType?: string }, void>(({ courseId, topicId, lessonId, taskId, taskType }) => {
    if (!taskType) return;
    TaskFailed(courseId, topicId, lessonId, taskId, taskType);
});

export type ITaskLayoutUIContract = typeof taskLayoutUiContract;

const taskLayoutUiContract = new ModelContract(
    {
        $taskLayoutConfigById,
        $taskLayoutStateById,
        $isCorrectById,
        $isAllSelectedById,
        $shouldShowSubmitById,
        $shouldDisableSubmitById,
        $shouldShowHintBtnById,
        $effectiveShowHintById,
        $optionStatusMapById,
        $usesSavedAnswerById,
    },
    {
        taskLayoutInstanceMounted,
        taskLayoutInstanceUnmounted,
        taskLayoutOptionChanged,
        taskLayoutSubmitClicked,
        taskLayoutResetClicked,
        taskLayoutHintClicked,
        taskAnswerChecked,
    },
    {},
    {}
);

export class TaskLayoutUIModule extends ModuleDescription<ITaskLayoutUIContract> {
    constructor(
        public tasksModule: ITasksContract,
        public enrollmentsModule: IEnrollmentsContract,
        public currentCourseModule: ICurrentCourseContract,
        public currentTopicModule: ICurrentTopicContract,
        public currentLessonModule: ICurrentLessonContract
    ) {
        super();
    }

    getDefaultContract() {
        return taskLayoutUiContract;
    }

    run(): void {
        reportTaskStateFx.use(async ({ lessonId, taskId, status, answer }) => {
            this.tasksModule.events.taskStateReportedV2({ lessonId, taskId, status, answer });
        });

        // Reads the state before applyStatePatch lands, like the sibling samples on the same clock.
        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                saved: $usesSavedAnswerById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutSubmitClicked,
            filter: (s, { taskId }) => Boolean(s.cfg[taskId]) && Boolean(s.lessonId),
            fn: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                return {
                    taskId,
                    lessonId: Number(s.lessonId),
                    courseId: Number(s.courseId),
                    topicId: Number(s.topicId),
                    taskType: cfg.taskType,
                    isCorrect: isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer),
                    usesSavedAnswer: s.saved[taskId] === true,
                    wasAlreadyCorrect: prev.showCorrectAnswer === true,
                };
            },
            target: taskAnswerChecked,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || !s.lessonId) return false;
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                const shouldTrackStart = !prev.hasStarted && Boolean(cfg.taskType) && Boolean(taskId) && !prev.submitted;
                return shouldTrackStart;
            },
            fn: (s, { taskId }) => ({
                courseId: Number(s.courseId),
                topicId: Number(s.topicId),
                lessonId: Number(s.lessonId),
                taskId,
                taskType: s.cfg[taskId].taskType,
            }),
            target: trackTaskStartedFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId }) => {
                if (!Boolean(s.cfg[taskId]) || !Boolean(s.lessonId)) return false;
                const prev = s.st[taskId];
                const cfg = s.cfg[taskId];
                if (cfg.taskType === "dropdown-gaps") return true;
                if (!prev?.submitted) return true;
                return false;
            },
            fn: (s, { taskId, option, payload }) => {
                const cfg = s.cfg[taskId];
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                const revealedOptions = prev.revealedOptions || {};
                const eventValue = extractEventValue(payload, option ?? "");
                const isMultipleChoice = Array.isArray(cfg.answer);

                if (cfg.allowContinueAfterWrongAnswer && revealedOptions[eventValue]) {
                    return {
                        taskId,
                        patch: {},
                    };
                }

                let nextSelected: any | null = prev.selected;
                if (cfg.isSingleOption && typeof payload !== "undefined") {
                    nextSelected = payload;
                } else {
                    if (isMultipleChoice) {
                        const currentSelected = Array.isArray(prev.selected) ? (prev.selected as string[]) : [];
                        nextSelected = currentSelected.includes(eventValue)
                            ? currentSelected.filter((item) => item !== eventValue)
                            : [...currentSelected, eventValue];
                    } else {
                        nextSelected = eventValue;
                    }
                }

                if (cfg.allowContinueAfterWrongAnswer && Array.isArray(nextSelected)) {
                    const incorrectRevealed = Object.keys(revealedOptions).filter(opt => revealedOptions[opt] === 'incorrect');
                    const correctRevealed = Object.keys(revealedOptions).filter(opt => revealedOptions[opt] === 'correct');

                    nextSelected = nextSelected.filter(item => !incorrectRevealed.includes(item));

                    for (const correctOpt of correctRevealed) {
                        if (!nextSelected.includes(correctOpt)) {
                            nextSelected.push(correctOpt);
                        }
                    }
                }

                const shouldTrackStart = !prev.hasStarted && Boolean(cfg.taskType) && Boolean(taskId);
                const shouldSetInteractionFlag = cfg.allowContinueAfterWrongAnswer && !prev.userInteractedAfterSubmit;

                const patch: Partial<TaskInstanceState> = {
                    selected: nextSelected,
                    hasStarted: prev.hasStarted || shouldTrackStart
                };

                if (shouldSetInteractionFlag) {
                    patch.userInteractedAfterSubmit = true;
                }

                if (cfg.taskType === "dropdown-gaps" && isPlainObject(nextSelected)) {
                    const parsedAnswer = tryParseObject(String(cfg.answer));
                    if (parsedAnswer) {
                        const prevSel = isPlainObject(prev.selected)
                            ? (prev.selected as Record<string, unknown>)
                            : {};
                        const prevExcluded = prev.dropdownGapExcludedById || {};
                        const prevLocked = prev.dropdownGapLockedCorrect || {};
                        const nextExcluded: Record<string, string[]> = { ...prevExcluded };
                        const nextLocked: Record<string, string> = { ...prevLocked };

                        for (const key of Object.keys(parsedAnswer)) {
                            if (nextLocked[key]) continue;
                            const newVal = (nextSelected as Record<string, unknown>)[key];
                            if (newVal === null || typeof newVal === "undefined" || newVal === "") continue;
                            const oldVal = prevSel[key];
                            if (newVal === oldVal) continue;
                            const newStr = String(newVal);
                            const correctVal = String(parsedAnswer[key]);
                            if (newStr === correctVal) {
                                nextLocked[key] = newStr;
                            } else {
                                const list = nextExcluded[key] ? [...nextExcluded[key]] : [];
                                if (!list.includes(newStr)) list.push(newStr);
                                nextExcluded[key] = list;
                            }
                        }

                        patch.dropdownGapExcludedById = nextExcluded;
                        patch.dropdownGapLockedCorrect = nextLocked;

                        const allLocked = Object.keys(parsedAnswer).every((k) => Boolean(nextLocked[k]));
                        if (allLocked && prev.submitted && !prev.showCorrectAnswer) {
                            patch.showCorrectAnswer = true;
                            patch.showWrongAnswer = false;
                        }
                    }
                }

                return {
                    taskId,
                    patch,
                };
            },
            target: applyStatePatch,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || !cfg.autoSubmitOnSelect) return false;
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                if (prev.submitted) return false;
                return true;
            },
            fn: (_, { taskId }) => ({ taskId }),
            target: taskLayoutSubmitClicked,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId, payload }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || cfg.taskType !== "dropdown-gaps") return false;
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                if (prev.submitted) return false;
                const parsedAnswer = tryParseObject(String(cfg.answer));
                if (!parsedAnswer) return false;
                const sel = isPlainObject(payload) ? (payload as Record<string, unknown>) : null;
                if (!sel) return false;
                for (const key of Object.keys(parsedAnswer)) {
                    const v = sel[key];
                    if (v === null || typeof v === "undefined" || v === "") return false;
                }
                return true;
            },
            fn: (_, { taskId }) => ({ taskId }),
            target: taskLayoutSubmitClicked,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId, payload }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || cfg.taskType !== "dropdown-gaps") return false;
                if (!s.lessonId) return false;
                const prev = s.st[taskId];
                if (!prev || !prev.submitted) return false;
                if (!isPlainObject(payload)) return false;
                return true;
            },
            fn: (s, { taskId, payload }) => {
                const cfg = s.cfg[taskId];
                const parsedAnswer = tryParseObject(String(cfg.answer));
                const newSel = isPlainObject(payload)
                    ? (payload as Record<string, unknown>)
                    : {};
                let isCorrect = false;
                if (parsedAnswer) {
                    isCorrect = Object.keys(parsedAnswer).every(
                        (k) => String(newSel[k] ?? "") === String(parsedAnswer[k])
                    );
                }
                const status: "completed" | "failed" = isCorrect ? "completed" : "failed";
                return {
                    lessonId: Number(s.lessonId),
                    taskId,
                    status,
                    answer: JSON.stringify(payload),
                };
            },
            target: reportTaskStateFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId, payload }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || cfg.taskType !== "dropdown-gaps") return false;
                if (!s.lessonId) return false;
                const prev = s.st[taskId];
                if (!prev || !prev.submitted) return false;
                if (!isPlainObject(payload)) return false;
                const parsedAnswer = tryParseObject(String(cfg.answer));
                if (!parsedAnswer) return false;
                const newSel = payload as Record<string, unknown>;
                return Object.keys(parsedAnswer).every(
                    (k) => String(newSel[k] ?? "") === String(parsedAnswer[k])
                );
            },
            fn: (s, { taskId }) => ({
                courseId: Number(s.courseId),
                topicId: Number(s.topicId),
                lessonId: Number(s.lessonId),
                taskId,
                taskType: s.cfg[taskId].taskType,
            }),
            target: trackTaskCompletedFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutOptionChanged,
            filter: (s, { taskId, payload }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || cfg.taskType !== "dropdown-gaps") return false;
                if (!s.lessonId) return false;
                const prev = s.st[taskId];
                if (!prev || !prev.submitted) return false;
                if (!isPlainObject(payload)) return false;
                const parsedAnswer = tryParseObject(String(cfg.answer));
                if (!parsedAnswer) return false;
                const newSel = payload as Record<string, unknown>;
                return !Object.keys(parsedAnswer).every(
                    (k) => String(newSel[k] ?? "") === String(parsedAnswer[k])
                );
            },
            fn: (s, { taskId }) => ({
                courseId: Number(s.courseId),
                topicId: Number(s.topicId),
                lessonId: Number(s.lessonId),
                taskId,
                taskType: s.cfg[taskId].taskType,
            }),
            target: trackTaskFailedFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutSubmitClicked,
            filter: (s, { taskId }) => Boolean(s.cfg[taskId]) && Boolean(s.lessonId),
            fn: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                const isCorrect = isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer);

                let nextRevealedOptions = prev.revealedOptions || {};
                if (cfg.allowContinueAfterWrongAnswer) {
                    if (Array.isArray(prev.selected) && Array.isArray(cfg.answer)) {
                        const newRevealed: Record<string, 'correct' | 'incorrect'> = { ...nextRevealedOptions };
                        for (const opt of prev.selected) {
                            const isOptionCorrect = cfg.answer.includes(opt);
                            newRevealed[opt] = isOptionCorrect ? 'correct' : 'incorrect';
                        }
                        nextRevealedOptions = newRevealed;
                    } else if (typeof prev.selected === 'string' && typeof cfg.answer === 'string') {
                        const newRevealed: Record<string, 'correct' | 'incorrect'> = { ...nextRevealedOptions };
                        const isOptionCorrect = prev.selected === cfg.answer;
                        newRevealed[prev.selected] = isOptionCorrect ? 'correct' : 'incorrect';
                        nextRevealedOptions = newRevealed;
                    }
                }

                const patch: Partial<TaskInstanceState> = {
                    showHint: false,
                    showCorrectAnswer: isCorrect,
                    showWrongAnswer: !isCorrect,
                    submitted: true,
                };

                if (cfg.allowContinueAfterWrongAnswer) {
                    patch.revealedOptions = nextRevealedOptions;
                }

                if (cfg.taskType === "dropdown-gaps" && !isCorrect && isPlainObject(prev.selected)) {
                    const parsedAnswer = tryParseObject(String(cfg.answer));
                    if (parsedAnswer) {
                        const prevExcluded = prev.dropdownGapExcludedById || {};
                        const nextExcluded: Record<string, string[]> = { ...prevExcluded };
                        const nextLocked: Record<string, string> = {};
                        for (const key of Object.keys(parsedAnswer)) {
                            const correctVal = String((parsedAnswer as Record<string, unknown>)[key]);
                            const selVal = (prev.selected as Record<string, unknown>)[key];
                            if (selVal === null || typeof selVal === "undefined" || selVal === "") continue;
                            const selStr = String(selVal);
                            if (selStr === correctVal) {
                                nextLocked[key] = correctVal;
                            } else {
                                const list = nextExcluded[key] ? [...nextExcluded[key]] : [];
                                if (!list.includes(selStr)) list.push(selStr);
                                nextExcluded[key] = list;
                            }
                        }
                        patch.dropdownGapExcludedById = nextExcluded;
                        patch.dropdownGapLockedCorrect = nextLocked;
                    }
                }

                return {
                    taskId,
                    patch,
                };
            },
            target: applyStatePatch,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutSubmitClicked,
            filter: (s, { taskId }) => Boolean(s.cfg[taskId]) && Boolean(s.lessonId),
            fn: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                const isCorrect = isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer);
                let answerToSend = prev.selected;
                if (cfg.allowContinueAfterWrongAnswer && isCorrect && Array.isArray(cfg.answer) && Array.isArray(prev.selected)) {
                    answerToSend = prev.selected.filter((item: any) => cfg.answer.includes(item));
                }
                const answerStr = cfg.isSingleAnswer ? String(answerToSend ?? "") : JSON.stringify(answerToSend);
                return {
                    lessonId: Number(s.lessonId),
                    taskId,
                    status: isCorrect ? ("completed" as const) : ("failed" as const),
                    answer: answerStr,
                };
            },
            target: reportTaskStateFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutSubmitClicked,
            filter: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || !s.lessonId) return false;
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                return isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer) && Boolean(cfg.taskType);
            },
            fn: (s, { taskId }) => ({
                courseId: Number(s.courseId),
                topicId: Number(s.topicId),
                lessonId: Number(s.lessonId),
                taskId,
                taskType: s.cfg[taskId].taskType,
            }),
            target: trackTaskCompletedFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutSubmitClicked,
            filter: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                if (!cfg || !s.lessonId) return false;
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                return !isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer) && Boolean(cfg.taskType);
            },
            fn: (s, { taskId }) => ({
                courseId: Number(s.courseId),
                topicId: Number(s.topicId),
                lessonId: Number(s.lessonId),
                taskId,
                taskType: s.cfg[taskId].taskType,
            }),
            target: trackTaskFailedFx,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutResetClicked,
            filter: (s, { taskId }) => Boolean(s.cfg[taskId]) && Boolean(s.lessonId),
            fn: (s, { taskId }) => {
                const cfg = s.cfg[taskId];
                const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                const resetAfterSuccess = Boolean(prev.showCorrectAnswer);
                let nextSelected: any = Array.isArray(cfg.answer) ? [] : null;
                if (cfg.taskType === "sort-answers") {
                    const wasCorrect = isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer);
                    nextSelected = wasCorrect ? (Array.isArray(cfg.answer) ? [] : null) : prev.selected;
                } else if (cfg.taskType === "fill-in-the-blanks" && !resetAfterSuccess && Array.isArray(cfg.answer)) {
                    const answerArr = cfg.answer;
                    const sel = Array.isArray(prev.selected) ? prev.selected : [];
                    const len = Math.max(answerArr.length, sel.length);
                    nextSelected = new Array(len).fill(null) as (string | null)[];
                    for (let i = 0; i < len; i += 1) {
                        const v = sel[i];
                        if (v === null || typeof v === "undefined" || v === "") continue;
                        if (answerArr[i] === v) nextSelected[i] = v;
                    }
                } else if (cfg.taskType === "dropdown-gaps") {
                    const parsedAnswer = tryParseObject(String(cfg.answer));
                    if (resetAfterSuccess) {
                        nextSelected = {};
                    } else if (parsedAnswer) {
                        const locked = prev.dropdownGapLockedCorrect || {};
                        nextSelected = {};
                        for (const key of Object.keys(parsedAnswer)) {
                            nextSelected[key] = locked[key] ?? "";
                        }
                    } else {
                        nextSelected = {};
                    }
                }
                const isSingleStringAnswer =
                    typeof cfg.answer === "string" && !Array.isArray(cfg.answer) && !cfg.isSingleOption;
                const isMultipleStringAnswer =
                    Array.isArray(cfg.answer) && !cfg.isSingleOption;
                const revealedOpts = prev.revealedOptions || {};
                const revealedCount = Object.keys(revealedOpts).length;
                const allOptionsRevealed = revealedCount === cfg.options.length;

                const preserveWrongRevealsOnRetry =
                    cfg.allowContinueAfterWrongAnswer &&
                    isSingleStringAnswer &&
                    !resetAfterSuccess &&
                    revealedCount > 0 &&
                    !prev.showCorrectAnswer;
                const preserveRevealedOnRetry =
                    cfg.allowContinueAfterWrongAnswer &&
                    isMultipleStringAnswer &&
                    !resetAfterSuccess &&
                    revealedCount > 0 &&
                    !allOptionsRevealed &&
                    !prev.showCorrectAnswer;

                let correctRevealedCoversAnswer = false;
                if (preserveRevealedOnRetry) {
                    const correctRevealedSorted = Object.keys(revealedOpts)
                        .filter(k => revealedOpts[k] === 'correct')
                        .sort();
                    const ansSorted = [...(cfg.answer as string[])].sort();
                    correctRevealedCoversAnswer =
                        correctRevealedSorted.length === ansSorted.length &&
                        correctRevealedSorted.every((v, i) => v === ansSorted[i]);
                }
                const effectivePreserveRevealedOnRetry = preserveRevealedOnRetry && !correctRevealedCoversAnswer;

                const preservedRevealed = preserveWrongRevealsOnRetry
                    ? Object.fromEntries(Object.entries(revealedOpts).filter(([, v]) => v === "incorrect"))
                    : effectivePreserveRevealedOnRetry
                        ? { ...revealedOpts }
                        : {};

                if (preserveRevealedOnRetry) {
                    const correctRevealed = Object.keys(revealedOpts).filter(k => revealedOpts[k] === 'correct');
                    nextSelected = correctRevealed;
                }

                const patch: Partial<TaskInstanceState> = {
                    showHint: false,
                    showCorrectAnswer: false,
                    showWrongAnswer: false,
                    submitted: false,
                    selected: nextSelected,
                    revealedOptions: (preserveWrongRevealsOnRetry || effectivePreserveRevealedOnRetry) ? preservedRevealed : {},
                    userInteractedAfterSubmit: true,
                };
                if (cfg.taskType === "dropdown-gaps" && resetAfterSuccess) {
                    patch.dropdownGapExcludedById = {};
                    patch.dropdownGapLockedCorrect = {};
                }

                return {
                    taskId,
                    patch,
                };
            },
            target: applyStatePatch,
        });

        sample({
            source: {
                cfg: $taskLayoutConfigById,
                st: $taskLayoutStateById,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                topicId: this.currentTopicModule.stores.$currentTopicId,
                lessonId: this.currentLessonModule.stores.$currentLessonId,
            },
            clock: taskLayoutResetClicked,
            filter: (s, { taskId, silent }) => {
                if (silent) return false;
                if (!Boolean(s.cfg[taskId]) || !Boolean(s.lessonId)) return false;
                const cfg = s.cfg[taskId];
                if (cfg.taskType === "sort-answers") {
                    const prev = s.st[taskId] ?? defaultStateForAnswer(cfg.answer);
                    return isAnswerCorrect(prev.selected, cfg.answer, cfg.taskType, cfg.checkAnswerCorrect, cfg.allowContinueAfterWrongAnswer);
                }
                return true;
            },
            fn: (s, { taskId }) => {
                return {
                    lessonId: Number(s.lessonId),
                    taskId,
                    status: "reset" as const,
                    answer: "",
                };
            },
            target: reportTaskStateFx,
        });

        sample({
            source: { cfg: $taskLayoutConfigById },
            clock: taskLayoutHintClicked,
            filter: (s, { taskId }) => Boolean(s.cfg[taskId]) && s.cfg[taskId].inlineHintEnabled,
            fn: (_, { taskId }) => ({ taskId, patch: { showHint: true } }),
            target: applyStatePatch,
        });

        sample({
            clock: taskLayoutInstanceMounted,
            source: { enrollmentsTasks: this.enrollmentsModule.stores.$enrollmentsTasks, st: $taskLayoutStateById },
            filter: ({ enrollmentsTasks, st }, cfg) => {
                const saved = enrollmentsTasks.find((t: any) => String(t.task_id) === String(cfg.taskId));
                if (!saved) return false;
                if (st[cfg.taskId]?.userInteractedAfterSubmit) return false;
                return true;
            },
            fn: ({ enrollmentsTasks }, cfg) => {
                const saved = enrollmentsTasks.find((t: any) => String(t.task_id) === String(cfg.taskId));
                if (!saved) return { taskId: cfg.taskId, patch: {} };

                const patch: Partial<TaskInstanceState> = {};
                if (cfg.isSingleAnswer) {
                    patch.selected = saved.answer;
                } else {
                    try {
                        const parsedAnswer = JSON.parse(saved.answer);
                        patch.selected = parsedAnswer;
                    } catch {
                        patch.selected = saved.answer;
                    }
                }
                if (saved.status === UserEnrollmentStatus.Completed) {
                    patch.showCorrectAnswer = true;
                    patch.showWrongAnswer = false;
                    patch.submitted = true;
                } else if (saved.status === UserEnrollmentStatus.Failed) {
                    patch.showWrongAnswer = true;
                    patch.showCorrectAnswer = false;
                    patch.submitted = true;
                } else {
                    patch.showCorrectAnswer = false;
                    patch.showWrongAnswer = false;
                    patch.submitted = false;
                }

                if (cfg.allowContinueAfterWrongAnswer && saved.status === UserEnrollmentStatus.Failed) {
                    const selectedArr = Array.isArray(patch.selected) ? patch.selected as string[] : [];
                    const answerArr = Array.isArray(cfg.answer) ? cfg.answer as string[] : [cfg.answer];
                    const reconstructed: Record<string, 'correct' | 'incorrect'> = {};
                    for (const opt of selectedArr) {
                        reconstructed[opt] = answerArr.includes(opt) ? 'correct' : 'incorrect';
                    }
                    patch.revealedOptions = reconstructed;
                }

                if (cfg.taskType === "dropdown-gaps" && isPlainObject(patch.selected)) {
                    const parsedAnswer = tryParseObject(String(cfg.answer));
                    if (parsedAnswer) {
                        const sel = patch.selected as Record<string, unknown>;
                        const locked: Record<string, string> = {};
                        const excluded: Record<string, string[]> = {};
                        for (const key of Object.keys(parsedAnswer)) {
                            const v = sel[key];
                            if (v === null || typeof v === "undefined" || v === "") continue;
                            const selStr = String(v);
                            const ansStr = String(parsedAnswer[key]);
                            if (selStr === ansStr) {
                                locked[key] = selStr;
                            } else {
                                excluded[key] = [selStr];
                            }
                        }
                        patch.dropdownGapLockedCorrect = locked;
                        patch.dropdownGapExcludedById = excluded;
                    }
                }

                return { taskId: cfg.taskId, patch };
            },
            target: applyStatePatch,
        });

        sample({
            clock: taskLayoutInstanceMounted,
            source: { enrollmentsTasks: this.enrollmentsModule.stores.$enrollmentsTasks, st: $taskLayoutStateById },
            filter: ({ enrollmentsTasks, st }, cfg) => {
                const saved = enrollmentsTasks.find((t: any) => String(t.task_id) === String(cfg.taskId));
                if (!saved) return false;
                if (st[cfg.taskId]?.userInteractedAfterSubmit) return false;
                return true;
            },
            fn: (_, cfg) => ({ taskId: cfg.taskId, value: true }),
            target: setUsesSavedAnswer,
        });

        sample({
            clock: this.enrollmentsModule.stores.$enrollmentsTasks,
            source: { enrollmentsTasks: this.enrollmentsModule.stores.$enrollmentsTasks, cfg: $taskLayoutConfigById, st: $taskLayoutStateById },
            fn: ({ enrollmentsTasks, cfg, st }) => {
                const batch: Record<string, Partial<TaskInstanceState>> = {};
                for (const id of Object.keys(cfg)) {
                    const localState = st[id];

                    if (
                        localState?.submitted ||
                        localState?.showCorrectAnswer ||
                        localState?.showWrongAnswer ||
                        localState?.userInteractedAfterSubmit
                    ) {
                        continue;
                    }

                    const saved = enrollmentsTasks.find((t: any) => String(t.task_id) === String(id));
                    if (!saved) continue;
                    const patch: Partial<TaskInstanceState> = {};
                    if (cfg[id].isSingleAnswer) {
                        patch.selected = saved.answer;
                    } else {
                        try {
                            const parsedAnswer = JSON.parse(saved.answer);
                            patch.selected = parsedAnswer;
                        } catch {
                            patch.selected = saved.answer;
                        }
                    }
                    if (saved.status === UserEnrollmentStatus.Completed) {
                        patch.showCorrectAnswer = true;
                        patch.showWrongAnswer = false;
                        patch.submitted = true;
                    } else if (saved.status === UserEnrollmentStatus.Failed) {
                        patch.showWrongAnswer = true;
                        patch.showCorrectAnswer = false;
                        patch.submitted = true;
                    } else {
                        patch.showCorrectAnswer = false;
                        patch.showWrongAnswer = false;
                        patch.submitted = false;
                    }

                    if (cfg[id].allowContinueAfterWrongAnswer && saved.status === UserEnrollmentStatus.Failed) {
                        const selectedArr = Array.isArray(patch.selected) ? patch.selected as string[] : [];
                        const answerArr = Array.isArray(cfg[id].answer) ? cfg[id].answer as string[] : [cfg[id].answer];
                        const reconstructed: Record<string, 'correct' | 'incorrect'> = {};
                        for (const opt of selectedArr) {
                            reconstructed[opt] = answerArr.includes(opt) ? 'correct' : 'incorrect';
                        }
                        patch.revealedOptions = reconstructed;
                    }

                    if (cfg[id].taskType === "dropdown-gaps" && isPlainObject(patch.selected)) {
                        const parsedAnswer = tryParseObject(String(cfg[id].answer));
                        if (parsedAnswer) {
                            const sel = patch.selected as Record<string, unknown>;
                            const locked: Record<string, string> = {};
                            const excluded: Record<string, string[]> = {};
                            for (const key of Object.keys(parsedAnswer)) {
                                const v = sel[key];
                                if (v === null || typeof v === "undefined" || v === "") continue;
                                const selStr = String(v);
                                const ansStr = String(parsedAnswer[key]);
                                if (selStr === ansStr) {
                                    locked[key] = selStr;
                                } else {
                                    excluded[key] = [selStr];
                                }
                            }
                            patch.dropdownGapLockedCorrect = locked;
                            patch.dropdownGapExcludedById = excluded;
                        }
                    }
                    batch[id] = patch;
                }
                return batch;
            },
            target: applyEnrollmentSync,
        });


    }
}


